import { useMutation } from '@tanstack/react-query';

import get from 'lodash/get';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  Api,
  CreateDraftEmployeeSchedulePatternDto,
  SchedulePatternApiSchedulePatternsSyncLegacyEmployeeSchedulePatternsToProPostRequest,
  useApi,
} from '../index';

export const useGetEmployeeDraftPattern = () => {
  const api = useApi();
  return useMutation((employeeId: number) =>
    api.schedulePatternApi
      .schedulePatternsDraftPatternsEmployeeIdGet({ employeeId })
      .then((response) => response.data.data ?? {})
  );
};

const saveDraftPattern =
  (api: Api) =>
  async (
    createDraftEmployeeSchedulePatternDto: CreateDraftEmployeeSchedulePatternDto
  ) => {
    return api.schedulePatternApi
      .schedulePatternsSaveDraftPost({
        createDraftEmployeeSchedulePatternDto,
      })
      .then((response) => response.data.data || {});
  };

export const useSaveDraftPattern = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  return useMutation(saveDraftPattern(api), {
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};

export type UseSaveDraftPatternResponse = NonNullable<
  ReturnType<typeof useSaveDraftPattern>['data']
>;

const getUserOverBudgetingPermission = (api: Api) => async () => {
  return api.schedulePatternApi
    .schedulePatternsUserpermissionOverbudgetGet({})
    .then((response) => response.data.data || false);
};

export const useGetUserOverBudgetingPermission = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  return useMutation(getUserOverBudgetingPermission(api), {
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const confirmOverBudgeting = (api: Api) => async (draftPatternId: number) => {
  return api.schedulePatternApi
    .schedulePatternsConfirmOverbudgetingDraftPatternIdPost({
      draftPatternId,
    })
    .then((response) => response.data);
};

export const useConfirmOverBudgeting = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(confirmOverBudgeting(api), {
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const discardDraftPattern = (api: Api) => async (draftPatternId: number) => {
  return api.schedulePatternApi
    .schedulePatternsDiscardDraftDraftPatternIdPost({
      draftPatternId,
    })
    .then((response) => response.data);
};

export const useDiscardDraftPattern = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(discardDraftPattern(api), {
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const publishEmployeeSchedulePattern =
  (api: Api) => async (draftPatternId: number) => {
    return api.schedulePatternApi
      .schedulePatternsPublishDraftDraftPatternIdPost({
        draftPatternId,
      })
      .then((response) => response.data.data || {});
  };

export const usePublishEmployeeSchedulePattern = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  return useMutation(publishEmployeeSchedulePattern(api), {
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const useSyncLegacyEmployeeSchedulePattern = () => {
  const api = useApi();
  return useMutation(
    (
      body: SchedulePatternApiSchedulePatternsSyncLegacyEmployeeSchedulePatternsToProPostRequest
    ) =>
      api.schedulePatternApi.schedulePatternsSyncLegacyEmployeeSchedulePatternsToProPost(
        body
      )
  );
};
